// LexiquePage.tsx
import { FC } from 'react';
import { Box, Typography } from '@mui/material';
import { Helmet } from 'react-helmet-async';

interface LexiqueEntry {
    terme: string;
    definition: string;
}

const LEXIQUE: LexiqueEntry[] = [
    {
        terme: 'Amateur',
        definition: "Catégorie de course réservée aux jockeys ou drivers non professionnels, souvent propriétaires ou éleveurs des chevaux qu'ils montent ou conduisent.",
    },
    {
        terme: 'Autostart',
        definition: "Type de départ où les chevaux s'alignent derrière une voiture équipée d'ailes mobiles, qui se referment au moment du lancement de la course. Utilisé principalement en trot attelé.",
    },
    {
        terme: 'Avis d\'entraîneur',
        definition: "Indication donnée par l'entraîneur sur les intentions ou les attentes concernant la performance de son cheval avant une course. Un avis positif signale généralement une préparation jugée satisfaisante.",
    },
    {
        terme: 'Catégorie',
        definition: "Classement des courses selon le niveau des chevaux autorisés à y participer, basé notamment sur leurs gains ou leurs performances passées. Les catégories permettent de faire courir des chevaux de niveau comparable entre eux.",
    },
    {
        terme: 'Corde',
        definition: "Le bord intérieur de la piste, le plus court chemin entre le départ et l'arrivée. Le sens de course peut se dérouler à corde gauche ou à corde droite selon les hippodromes.",
    },
    {
        terme: 'Cote de référence',
        definition: "La cote établie tôt avant la course, avant les derniers mouvements du marché. Elle sert de point de comparaison avec la cote directe pour identifier les chevaux massivement joués ou délaissés dans les derniers instants.",
    },
    {
        terme: 'Cote directe',
        definition: "La cote affichée juste avant le départ de la course, la plus à jour. Elle intègre les derniers mouvements de mise et reflète l'opinion la plus récente du marché.",
    },
    {
        terme: 'Course à conditions',
        definition: "Course dont les conditions de participation (âge, sexe, gains, victoires) sont définies à l'avance, par opposition aux handicaps où le poids ou la distance est ajusté individuellement.",
    },
    {
        terme: 'Déferré',
        definition: "Se dit d'un cheval qui court sans fers, ou sans fers à certains membres. Le déferrage allège le cheval et peut améliorer sa vitesse, mais expose davantage ses sabots.",
    },
    {
        terme: 'Disqualification',
        definition: "Sanction retirant à un cheval le bénéfice de son classement à l'arrivée, généralement en raison d'une faute d'allure (galop en trot) ou d'une infraction au règlement de course.",
    },
    {
        terme: 'Driver',
        definition: "Le conducteur du sulky en trot attelé, équivalent du jockey en monté ou en plat.",
    },
    {
        terme: 'Écart',
        definition: "La distance, exprimée en longueurs ou en mesures spécifiques (tête, encolure, nez), séparant un cheval du concurrent qui le précède à l'arrivée.",
    },
    {
        terme: 'Éleveur',
        definition: "La personne ou structure ayant élevé le cheval depuis sa naissance, distincte du propriétaire actuel et de l'entraîneur qui le prépare aux courses.",
    },
    {
        terme: 'Engagement',
        definition: "L'inscription d'un cheval à une course donnée, effectuée par son entraîneur avant que la liste des partants ne soit définitivement arrêtée.",
    },
    {
        terme: 'Ferré',
        definition: "Se dit d'un cheval équipé de fers à ses quatre membres, la configuration standard. S'oppose au déferrage, qui retire les fers à certains ou tous les membres.",
    },
    {
        terme: 'Groupe (course de)',
        definition: "Catégorie de course de très haut niveau (Groupe I, II ou III), réservée aux meilleurs chevaux d'une génération ou d'une discipline, avec des dotations généralement élevées.",
    },
    {
        terme: 'Handicap',
        definition: "Course de plat où chaque cheval se voit attribuer un poids différent selon son niveau estimé, afin d'égaliser au maximum les chances de victoire entre tous les partants.",
    },
    {
        terme: 'Hippodrome',
        definition: "Le lieu où se déroulent les courses hippiques, comprenant la piste de course et les installations annexes.",
    },
    {
        terme: 'Jockey',
        definition: "Le cavalier qui monte un cheval en course de plat, d'obstacle ou de trot monté.",
    },
    {
        terme: 'Musique',
        definition: "La suite des résultats obtenus par un cheval lors de ses dernières courses, codée sous forme de lettres et de chiffres (par exemple 1a2a3a pour trois places consécutives). Elle donne un aperçu rapide de sa forme récente.",
    },
    {
        terme: 'Œillères',
        definition: "Pièces de harnachement limitant le champ de vision latéral du cheval, destinées à réduire les distractions ou à corriger certains comportements pendant la course.",
    },
    {
        terme: 'Ordre d\'arrivée',
        definition: "Le classement final des chevaux à l'issue d'une course, du premier au dernier.",
    },
    {
        terme: 'Partant',
        definition: "Cheval effectivement présent au départ d'une course, par opposition aux chevaux engagés qui ont finalement été retirés avant la course.",
    },
    {
        terme: 'Pénétromètre',
        definition: "Instrument mesurant la souplesse ou la dureté du terrain sur une piste de plat, exprimée par une valeur chiffrée. Un terrain souple ou lourd favorise certains profils de chevaux, un terrain sec et dur en favorise d'autres.",
    },
    {
        terme: 'Place à la corde',
        definition: "La position de départ attribuée à un cheval par rapport à la corde intérieure de la piste. Une place proche de la corde peut représenter un avantage ou un inconvénient selon le type de départ et le nombre de partants.",
    },
    {
        terme: 'Plafond',
        definition: "Montant de gains au-delà duquel un cheval est soumis à un recul de distance dans certaines catégories de courses, afin d'équilibrer les chances entre chevaux de niveaux différents.",
    },
    {
        terme: 'Protégé',
        definition: "Se dit d'un cheval équipé de plaques de protection plutôt que de fers classiques, une configuration intermédiaire entre le ferrage complet et le déferrage.",
    },
    {
        terme: 'Recul',
        definition: "Distance supplémentaire imposée à un cheval ayant dépassé un certain plafond de gains, afin de compenser sa force par rapport au reste du peloton.",
    },
    {
        terme: 'Réduction kilométrique',
        definition: "Le temps moyen mis par un cheval pour parcourir un kilomètre lors d'une course de trot, exprimé en minutes, secondes et dixièmes. Plus la réduction est basse, plus le cheval a été rapide.",
    },
    {
        terme: 'Rentrée',
        definition: "La première course d'un cheval après une période sans compétition. Une rentrée peut concerner un jeune cheval qui débute, ou un cheval expérimenté qui revient après une longue absence.",
    },
    {
        terme: 'Robe',
        definition: "La couleur de la robe (du pelage) d'un cheval, utilisée notamment pour l'identifier (alezan, bai, gris, etc.).",
    },
    {
        terme: 'Supplément',
        definition: "Dispositif ou équipement additionnel porté par un cheval lors d'une course, pouvant influencer sa performance selon le contexte.",
    },
    {
        terme: 'Trot attelé',
        definition: "Discipline où le cheval tire un sulky (petite voiture légère à deux roues) conduit par un driver, sans jamais quitter l'allure du trot.",
    },
    {
        terme: 'Trot monté',
        definition: "Discipline où le cheval est monté directement par un jockey, sans sulky, tout en conservant l'allure du trot.",
    },
    {
        terme: 'Type de piste',
        definition: "La nature du revêtement d'une piste de plat (sable, herbe, fibré), qui influence significativement les temps de course et peut favoriser certains profils de chevaux.",
    },
];

const LexiquePage: FC = () => {
    const lettres = Array.from(new Set(LEXIQUE.map((e) => e.terme[0].toUpperCase()))).sort();

    return (
        <Box maxWidth={800} mx="auto" p={2} display="flex" flexDirection="column" alignItems="flex-start">
            <Helmet>
                <title>Lexique du trot et du galop | ExpertTurf</title>
                <meta
                    name="description"
                    content="Tous les termes techniques des courses hippiques expliqués simplement : ferrure, cotes, recul, réduction kilométrique et plus encore."
                />
            </Helmet>

            <Typography variant="h3" component="h1" fontWeight={700} gutterBottom textAlign="left" width="100%">
                Lexique du trot et du galop
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" mb={4} textAlign="left" width="100%">
                Tous les termes techniques que vous croiserez sur nos pronostics, expliqués simplement.
            </Typography>

            <Box display="flex" flexWrap="wrap" gap={1} mb={4}>
                {lettres.map((lettre) => (
                    <Box
                        key={lettre}
                        component="a"
                        href={`#lettre-${lettre}`}
                        sx={{
                            width: 32,
                            height: 32,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '50%',
                            color: 'primary.main',
                            borderColor: "primary.main",
                            border: "1px solid",
                            fontWeight: 600,
                            fontSize: 14,
                            textDecoration: 'none',
                            '&:hover': { bgcolor: 'secondary.light', color: 'white' },
                        }}
                    >
                        {lettre}
                    </Box>
                ))}
            </Box>

            <Box display="flex" flexDirection="column" gap={3} width="100%">
                {lettres.map((lettre) => (
                    <Box key={lettre} id={`lettre-${lettre}`} width="100%">
                        <Typography
                            variant="h5"
                            fontWeight={700}
                            color="secondary.main"
                            mb={1.5}
                            textAlign="left"
                        >
                            {lettre}
                        </Typography>
                        <Box display="flex" flexDirection="column" gap={2}>
                            {LEXIQUE.filter((e) => e.terme[0].toUpperCase() === lettre).map((entry) => (
                                <Box key={entry.terme}>
                                    <Typography variant="subtitle1" fontWeight={700} textAlign="left">
                                        {entry.terme}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" textAlign="left">
                                        {entry.definition}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

export default LexiquePage;